import { colors as Colors } from '@nw/shared/ui';

/** Figma NIAT Portal TILE_UI — used when `tile_ui_theme` colors are missing or invalid. */
export const DEFAULT_TILE_UI_THEME = {
  selectedBackground: '#FDF2F2',
  selectedBorder: '#9B1C1C',
  defaultBackground: Colors['base-white'],
  defaultBorder: '#D1D5DB',
  textColor: Colors.pickledBluewood
} as const;

const HEX_COLOR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

function expandShortHex(hex: string): string {
  if (hex.length === 4 && hex.startsWith('#')) {
    const r = hex[1];
    const g = hex[2];
    const b = hex[3];
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return hex;
}

/** Returns a safe CSS hex color or `fallback` if the value is not valid hex. */
export function parseConfigHexColor(
  value: string | undefined,
  fallback: string
): string {
  if (value === undefined || value === null) {
    return fallback;
  }
  const trimmed = String(value).trim();
  if (!HEX_COLOR_RE.test(trimmed)) {
    return fallback;
  }
  return expandShortHex(trimmed);
}

/** Mirrors optional `tile_ui_theme` from form config. Only selected-state (and optional label) colors can be overridden; unselected tiles always use design defaults. */
export interface TileUiThemeConfig {
  selected_background?: string;
  selected_border?: string;
  text_color?: string;
}

export interface ResolvedTileUiTheme {
  selectedBackground: string;
  selectedBorder: string;
  defaultBackground: string;
  defaultBorder: string;
  textColor: string;
}

export function resolveTileUiTheme(
  config?: TileUiThemeConfig | null
): ResolvedTileUiTheme {
  const d = DEFAULT_TILE_UI_THEME;
  if (!config) {
    return {
      selectedBackground: d.selectedBackground,
      selectedBorder: d.selectedBorder,
      defaultBackground: d.defaultBackground,
      defaultBorder: d.defaultBorder,
      textColor: d.textColor
    };
  }

  return {
    selectedBackground: parseConfigHexColor(
      config.selected_background,
      d.selectedBackground
    ),
    selectedBorder: parseConfigHexColor(
      config.selected_border,
      d.selectedBorder
    ),
    defaultBackground: d.defaultBackground,
    defaultBorder: d.defaultBorder,
    textColor: parseConfigHexColor(config.text_color, d.textColor)
  };
}
